'use client';

import React, { isValidElement } from 'react';
import type { Ref } from 'react';
import { CheckIcon, ChevronRightIcon } from '@milanote/roma-icons';
import type { MenuItemProps as AriaMenuItemProps } from 'react-aria-components';
import { MenuItem as AriaMenuItem } from 'react-aria-components';
import { cx } from '../../../utils/cx';
import { isReactComponent } from '../../../utils/isReactComponent';
import useId from '../../../utils/useId';
import type { MenuItemStateProps, MenuItemStructuredProps, MenuSize } from './menuTypes';
import { MenuItemBase as MenuItemRootBase } from './MenuItemBase';
import { resolveMenuItemVariantStyles, styles } from './menuItemStyles';

const MENU_ITEM_DISPLAY_NAME = 'RomaMenuItem';

/**
 * Resolves the accessible text value for react-aria when callers do not provide one explicitly.
 */
const resolveMenuItemTextValue = (textValue: string | undefined, label: React.ReactNode): string | undefined => {
    if (textValue !== undefined) return textValue;
    if (typeof label === 'string' || typeof label === 'number') return String(label);
    return undefined;
};

/**
 * Resolves trailing content while keeping submenu and selected affordances in priority order.
 */
const resolveTrailingContent = ({
    hasSubmenu,
    isSelected,
    trailing,
    submenuIndicator,
    selectedIndicator,
}: {
    hasSubmenu?: boolean;
    isSelected?: boolean;
    trailing?: React.ReactNode;
    submenuIndicator: React.ReactNode;
    selectedIndicator: React.ReactNode;
}): React.ReactNode | null => {
    if (hasSubmenu) return submenuIndicator;
    if (isSelected) return selectedIndicator;
    if (trailing) return trailing;
    return null;
};

/**
 * Resolves leading content so icon components receive the shared menu icon sizing.
 */
const resolveLeadingContent = ({
    leading,
    iconClassName,
}: {
    leading: MenuItemStructuredProps['leading'];
    iconClassName: string;
}): React.ReactNode => {
    if (!leading) return null;
    if (isValidElement(leading)) return leading;
    if (isReactComponent(leading)) {
        const Leading = leading;
        return <Leading data-icon="leading" className={iconClassName} />;
    }

    return leading;
};

export type MenuItemStructuredBaseProps = MenuItemStructuredProps &
    MenuItemStateProps & {
        size?: MenuSize;
        className?: string;
        labelId?: string;
        descriptionId?: string;
    };

/**
 * Renders the shared menu item layout for structured menu content.
 */
export const MenuItemStructuredBase: React.FC<MenuItemStructuredBaseProps> = ({
    size,
    className,
    leading,
    label,
    description,
    trailing,
    destructive,
    isSelected,
    hasSubmenu,
    isOpen,
    isDisabled,
    isFocused,
    isFocusVisible,
    isHovered,
    labelId,
    descriptionId,
}) => {
    const variantStyles = resolveMenuItemVariantStyles({ destructive, isDisabled });
    const rootClassName = isDisabled
        ? styles.variants.disabled.root
        : destructive
          ? styles.variants.destructive.root
          : styles.variants.default.root;
    const submenuIndicator = (
        <ChevronRightIcon aria-hidden="true" className={cx(styles.common.trailingIcon, variantStyles.icon)} />
    );
    const selectedIndicator = (
        <CheckIcon aria-hidden="true" className={cx(styles.common.trailingIcon, variantStyles.icon)} />
    );

    const trailingContent = resolveTrailingContent({
        hasSubmenu,
        isSelected,
        trailing,
        submenuIndicator,
        selectedIndicator,
    });

    const leadingContent = resolveLeadingContent({
        leading,
        iconClassName: cx(styles.common.leadingIcon, variantStyles.icon),
    });

    return (
        <MenuItemRootBase
            size={size}
            className={cx(rootClassName, className)}
            hasDescription={Boolean(description)}
            hasLeading={Boolean(leading)}
            isSelected={isSelected}
            hasSubmenu={hasSubmenu}
            isOpen={isOpen}
            isDisabled={isDisabled}
            isFocused={isFocused}
            isFocusVisible={isFocusVisible}
            isHovered={isHovered}
        >
            <div className={styles.common.row}>
                {leadingContent && (
                    <span
                        aria-hidden="true"
                        data-icon="leading"
                        className={cx(styles.common.leading, variantStyles.icon)}
                    >
                        {leadingContent}
                    </span>
                )}

                <div className={styles.common.content}>
                    {label && (
                        <span id={labelId} className={styles.common.label}>
                            {label}
                        </span>
                    )}
                    {description && (
                        <span id={descriptionId} className={cx(styles.common.description, variantStyles.description)}>
                            {description}
                        </span>
                    )}
                </div>
            </div>

            {trailingContent && (
                <span
                    aria-hidden="true"
                    data-icon="trailing"
                    className={cx(styles.common.trailing, variantStyles.trailing)}
                >
                    {trailingContent}
                </span>
            )}
        </MenuItemRootBase>
    );
};

MenuItemStructuredBase.displayName = MENU_ITEM_DISPLAY_NAME;

type MenuItemCommonProps<T extends object> = Omit<AriaMenuItemProps<T>, 'children' | 'className' | 'textValue'> &
    MenuItemStructuredProps & {
        className?: AriaMenuItemProps<T>['className'];
        textValue?: string;
    };

export type MenuItemProps<T extends object> = MenuItemCommonProps<T>;

type MenuItemComponent = (<T extends object>(
    props: MenuItemProps<T> & { ref?: Ref<HTMLDivElement> },
) => React.ReactElement) & {
    displayName?: string;
};

/**
 * Renders a structured menu item backed by react-aria menu item state.
 */
export const MenuItem = React.forwardRef(function MenuItemInner<T extends object>(
    props: MenuItemProps<T>,
    ref: Ref<HTMLDivElement>,
): React.ReactElement {
    const { className, textValue, label, description, leading, trailing, destructive, ...ariaProps } = props;

    const resolvedTextValue = resolveMenuItemTextValue(textValue, label);
    const labelId = useId();
    const descriptionId = useId();
    const resolvedLabelId = label ? labelId : undefined;
    const resolvedDescriptionId = description ? descriptionId : undefined;

    return (
        <AriaMenuItem
            {...ariaProps}
            ref={ref as Ref<T>}
            textValue={resolvedTextValue}
            aria-labelledby={resolvedLabelId}
            aria-describedby={resolvedDescriptionId}
            className={(state) =>
                cx(
                    MENU_ITEM_DISPLAY_NAME,
                    styles.common.menuItem,
                    typeof className === 'function' ? className(state) : className,
                )
            }
        >
            {(state) => (
                <MenuItemStructuredBase
                    leading={leading}
                    label={label}
                    description={description}
                    trailing={trailing}
                    destructive={destructive}
                    isSelected={state.isSelected}
                    hasSubmenu={state.hasSubmenu}
                    isOpen={state.isOpen}
                    isDisabled={state.isDisabled}
                    isFocused={state.isFocused}
                    isFocusVisible={state.isFocusVisible}
                    isHovered={state.isHovered}
                    labelId={resolvedLabelId}
                    descriptionId={resolvedDescriptionId}
                />
            )}
        </AriaMenuItem>
    );
}) as MenuItemComponent;

MenuItem.displayName = MENU_ITEM_DISPLAY_NAME;
